// src/store/offlineStore.js
// Sync engine status visible to the UI layer.
// The sync engine (services/sync/engine.js) drives these values via onSyncEvent().
// Components read here to show offline indicators, pending badges, SOS failure modals.
// Not persisted — rebuilt from IDB on startup via the useSync hook.

import { create } from 'zustand';

export const useOfflineStore = create((set, get) => ({
  // ── Connectivity ───────────────────────────────────────────────
  online:        typeof navigator !== 'undefined' ? navigator.onLine : true,
  lastOnlineAt:  null,
  lastOfflineAt: null,

  // ── Sync queue counts ──────────────────────────────────────────
  // Mirrored from IDB SyncQueue.countByStatus() by the sync engine.
  queueCounts: {
    pending: 0,
    syncing: 0,
    done:    0,
    dead:    0,
  },

  // ── Dead letter items ──────────────────────────────────────────
  // Full SyncQueue records with status === 'dead'.
  // The engine pushes these here after exhausting retries.
  deadItems: [],

  // ── SOS failure ───────────────────────────────────────────────
  // Set when a create_sos item dies. Triggers the in-app emergency modal.
  sosFailure:      null,   // { syncId, body } | null
  sosSmsFallback:  null,   // { smsUrl, body, display } | null when SOS dead-letters

  // ── Last engine event ──────────────────────────────────────────
  // Most recent event emitted by the sync engine (for debugging / status UI).
  lastSyncEvent: null,

  // ── SW update ─────────────────────────────────────────────────
  swUpdateAvailable: false,

  // ── Actions ───────────────────────────────────────────────────
  setOnline: (online) => set((s) => ({
    online,
    lastOnlineAt:  online ? Date.now() : s.lastOnlineAt,
    lastOfflineAt: online ? s.lastOfflineAt : Date.now(),
  })),

  setQueueCounts: (counts) => set({ queueCounts: counts }),

  setSyncEvent: (event) => set({ lastSyncEvent: event }),

  addDeadItem: (item) => set((s) => ({
    deadItems: s.deadItems.some((d) => d.syncId === item.syncId)
      ? s.deadItems
      : [...s.deadItems, item],
  })),

  clearDeadItem: (syncId) => set((s) => ({
    deadItems: s.deadItems.filter((d) => d.syncId !== syncId),
  })),

  setSosFailure: (failure) => {
    set({ sosFailure: failure });
    // When an SOS write dies in the sync engine, immediately surface SMS fallback
    if (failure?.body) {
      import('@services/notifications/sms').then(({ buildSOSFallbackSMS }) => {
        const fb = buildSOSFallbackSMS({ body: failure.body, sosSubtype: 'other' });
        set({ sosSmsFallback: fb });
      }).catch(() => {});
    }
  },
  clearSosFailure: ()        => set({ sosFailure: null, sosSmsFallback: null }),

  setSwUpdateAvailable: (v) => set({ swUpdateAvailable: v }),

  // ── Derived ───────────────────────────────────────────────────
  get isIdle()        { const c = get().queueCounts; return c.pending === 0 && c.syncing === 0; },
  get hasPending()    { return get().queueCounts.pending > 0; },
  get hasDead()       { return get().deadItems.length > 0; },
  get hasSosFailed()  { return get().deadItems.some((d) => d.type === 'create_sos'); },
  get pendingCount()  { return get().queueCounts.pending; },
}));
